import {
    collection,
    query,
    getDocs,
    doc,
    writeBatch,
    QueryDocumentSnapshot,
    DocumentData,
    where,
    orderBy,
    limit,
    Query,
    deleteDoc,
    CollectionReference,
} from 'firebase/firestore';
import { db } from '../../../config/firebase';
import DbCollections from '../../../resources/enums/DbCollections';
import { Task, Option } from '../types';

const MAX_BATCH_SIZE = 10;

const mapSnapshotToTask = (doc: QueryDocumentSnapshot<DocumentData>) => {
    return {
        ...doc.data(),
        id: doc.id,
    } as Task;
};

const mapTaskToSnapshot = (task: Task) => {
    return {
        id: task.id,
        image: task.image,
        type: task.type,
        skills: task.skills ?? {},
        description: task.description,
        feedback: task.feedback ?? {},
        languageVideoUrl: task.languageVideoUrl ?? {},
    };
};

const mapSnapshotToOption = (doc: QueryDocumentSnapshot<DocumentData>) => {
    return {
        ...doc.data(),
        id: doc.id,
    } as Option;
};

const getOptions = async (taskId: string) => {
    const querySnapshot = await getDocs(query(collection(db, DbCollections.Tasks, taskId, DbCollections.Options)));
    const options = [] as Option[];
    querySnapshot.forEach((doc) => {
        options.push(mapSnapshotToOption(doc));
    });
    return options;
};

async function deleteCollection(collectionPath: CollectionReference<DocumentData>, batchSize: number) {
    const q = query(collectionPath, orderBy('__name__'), limit(batchSize));

    return new Promise((resolve, reject) => {
        deleteQueryBatch(q, resolve).catch(reject);
    });
}

async function deleteQueryBatch(q: Query<DocumentData>, resolve: (value?: unknown) => void) {
    const snapshot = await getDocs(q);

    const batchSize = snapshot.size;
    if (batchSize === 0) {
        resolve();
        return;
    }

    const batch = writeBatch(db);
    snapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
    });
    await batch.commit();

    deleteQueryBatch(q, resolve);
}

export const getTasksFirebase = async (ids: string[]) => {
    const collectionPath = collection(db, DbCollections.Tasks);
    const batches = [];
    const idList = [...ids];

    while (idList.length) {
        const batch = idList.splice(0, MAX_BATCH_SIZE);
        batches.push(getDocs(query(collectionPath, where('id', 'in', [...batch]))));
    }

    let tasks = [] as Task[];

    await Promise.all(batches).then((content) =>
        content.flat().map(async (querySnapshot) => {
            querySnapshot.forEach(async (doc) => {
                tasks.push(mapSnapshotToTask(doc));
            });
        }),
    );

    tasks = await Promise.all(
        tasks.map(
            async (task) =>
                ({
                    ...task,
                    options: await getOptions(task.id),
                } as Task),
        ),
    );

    return tasks.sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id));
};

export const saveTasksFirebase = async (tasks: Task[]) => {
    const batch = writeBatch(db);
    tasks.forEach((task) => {
        const docRef = doc(db, DbCollections.Tasks, task.id);
        batch.set(docRef, mapTaskToSnapshot(task));
        task.options?.forEach((option) => {
            const docRef = doc(db, DbCollections.Tasks, task.id, DbCollections.Options, option.id);
            batch.set(docRef, option);
        });
    });
    await batch.commit();
    return tasks;
};

export const deleteTaskFirebase = async (id: string) => {
    await deleteCollection(collection(db, DbCollections.Tasks, id, DbCollections.Options), MAX_BATCH_SIZE);
    await deleteDoc(doc(db, DbCollections.Tasks, id));
    return id;
};

export const deleteTasksFirebase = async (ids: string[]) => {
    ids.forEach(async (id) => {
        await deleteTaskFirebase(id);
    });
    return ids;
};
